import { useEffect, useRef } from 'react';
import { cn } from '../../../lib/styles';
import { Icon } from '../icon/icon.spark';
import { useFieldIds } from '../use-field-ids';
import type { CheckboxProps } from './types';

export function Checkbox({
  label,
  description,
  id,
  children,
  error,
  indeterminate,
  ...inputProps
}: CheckboxProps) {
  const { fieldId: inputId, descriptionId } = useFieldIds({
    id,
    hasDescription: !!description,
  });
  // `indeterminate` is a DOM property, not an attribute, so it must be set imperatively.
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = Boolean(indeterminate);
  }, [indeterminate]);

  return (
    <div className="grid grid-cols-[auto_1fr] items-center gap-x-2 text-sm text-spark-text-primary">
      <input
        {...inputProps}
        ref={ref}
        id={inputId}
        type="checkbox"
        aria-describedby={descriptionId}
        className={cn(
          // The hover and disabled fills are scoped to the unticked box: unscoped they outrank the
          // plugin's own `:checked` rule and wipe the brand fill out from under the white tick.
          'form-checkbox col-start-1 row-start-1 size-4 cursor-pointer rounded border-spark-border-default text-spark-primary shadow-xs not-checked:not-indeterminate:hover:bg-spark-control-fill-hover focus:border-spark-primary-accent focus:ring-spark-primary-accent disabled:cursor-not-allowed not-checked:not-indeterminate:disabled:bg-spark-surface-muted disabled:text-spark-text-secondary',
          error &&
            'border-spark-text-danger not-checked:not-indeterminate:bg-spark-danger-soft text-spark-danger-solid not-checked:not-indeterminate:hover:bg-spark-danger-soft-hover focus:border-spark-text-danger focus:ring-spark-text-danger not-checked:not-indeterminate:disabled:bg-spark-danger-soft-hover',
        )}
      />

      <label
        htmlFor={inputId}
        className={cn(
          'col-start-2 row-start-1 flex items-center gap-2',
          inputProps.disabled ? 'cursor-not-allowed text-spark-text-secondary' : 'cursor-pointer',
        )}
      >
        <span>{label}</span>
      </label>

      {description && (
        <p id={descriptionId} className="col-start-2 text-spark-text-secondary">
          {description}
        </p>
      )}

      {error && (
        <div className="col-start-2 flex items-center gap-1 text-spark-text-danger">
          <Icon name="xCircle" />
          {error}
        </div>
      )}

      {inputProps.checked && children && <div className="col-start-2 mt-1">{children}</div>}
    </div>
  );
}
